import React, { useEffect, useRef, useState } from 'react';
import CommonTable from 'components/common/CommonTable'; // Adjust the path as per your setup
import { Box, Button, Tooltip } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import roundFilterList from '@iconify/icons-ic/round-filter-list';
import { Icon } from '@iconify/react';
import HeaderBreadcrumbs from 'components/HeaderBreadcrumbs';
import { axios } from 'utils/axios';
import FiltersDrawer from 'components/_dashboard/contact/FilterDrawer';
import { CheckCircle, Cancel, PersonAdd } from '@mui/icons-material';
import useSettings from 'hooks/useSettings';
import { Container } from '@mui/material';
import CustomPaper from 'components/common/CustomPaper';
import { TextField } from '@mui/material';
import { InputAdornment } from '@mui/material';
import { GridSearchIcon } from '@mui/x-data-grid';

const defaultFilters = {
  search: '',
  pageSize: 10,
  postcode: '',
  dateFrom: '',
  dateTo: '',
  isOwner: '',
  isClient: '',
  accountRef: '',
  externalRef: '',
  councilArea: '',
  address: '',
  contactName: '',
  name: ''
};
const Contacts = () => {
  const navigate = useNavigate();
  const { themeStretch } = useSettings();

  const [contacts, setContacts] = useState<any>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [pageSize, setPageSize] = useState(10);
  const [filters, setFilters] = useState(defaultFilters);
  const debounceTimeout = useRef<NodeJS.Timeout | null>(null);

  const { enqueueSnackbar } = useSnackbar();

  const [totalRows, setTotalRows] = useState(0);

  const fetchContacts = async (isClear?: boolean, searchFilters?: any) => {
    setLoading(true);
    try {
      // Use default filters if isClear is true, otherwise use current filters
      const appliedFilters: any = isClear
        ? defaultFilters
        : searchFilters
        ? searchFilters
        : filters;

      const query = new URLSearchParams({
        page: page.toString(),
        ...Object.entries(appliedFilters)
          .filter(([key, value]) => value !== '' && value !== null && value !== undefined)
          .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {})
      }).toString();

      const response = await axios.get(`/rest/api/contacts/info?${query}`, {
        withCredentials: true
      });
      const data = response.data.data.data || [];
      setContacts(data);
      setTotalRows(response.data.data.pageMeta.totalRecords || 0);
    } catch (error) {
      console.error('Error fetching contacts:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContacts();
  }, [page, pageSize]);

  const columns = [
    { field: 'Name', headerName: 'Name', flex: 1, minWidth: 200 },
    { field: 'AccountRef', headerName: 'Account Ref', flex: 1, minWidth: 200 },
    { field: 'ContactName', headerName: 'Contact Name', flex: 1, minWidth: 250 },
    { field: 'Telephone', headerName: 'Telephone', flex: 1, minWidth: 250 },
    { field: 'Email', headerName: 'Email', flex: 1, minWidth: 200 },
    { field: 'add_line1', headerName: 'Address', flex: 2, minWidth: 400 },
    {
      field: 'IsClient',
      headerName: 'Client',
      flex: 1,
      minWidth: 150,
      renderCell: (params: any) =>
        params ? <CheckCircle color="success" /> : <Cancel color="error" />
    },
    {
      field: 'IsOwner',
      headerName: 'Owner',
      flex: 1,
      minWidth: 150,
      renderCell: (params: any) =>
        params ? <CheckCircle color="success" /> : <Cancel color="error" />
    },
    {
      field: 'IsHaulier',
      headerName: 'Haulier',
      flex: 1,
      minWidth: 150,
      renderCell: (params: any) =>
        params ? <CheckCircle color="success" /> : <Cancel color="error" />
    }
  ];

  const handleCreateContact = () => {
    navigate('/dashboard/contacts/new');
  };

  const handleRowClick = (params: any) => {
    console.log(params, 'This is the params');
    navigate(`/dashboard/contacts/details/${params.row.Id}`);
  };
  const handleSiteButtonClick = (params: any) => {
    navigate(`/dashboard/contacts/details/${params.row.Id}?tab=sites`);
  };

  const handleEdit = (id: number) => {
    navigate(`/dashboard/contacts/edit/${id}`);
  };

  const handleDeleteContact = async (id: number) => {
    try {
      await axios.delete(`/rest/api/contacts/info/${id}`);
      const newContacts = contacts.filter((el: any) => el.Id !== id);
      setContacts(newContacts);
      enqueueSnackbar('Delete Success', { variant: 'success' });
    } catch (error) {
      enqueueSnackbar('Delete Failed', { variant: 'success' });
    }
  };

  const clearFilters = () => {
    setFilters(defaultFilters);
    fetchContacts(true);
  };

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    const searchValue = event.target.value.toLowerCase();
    const updatedFilters = { ...filters, search: searchValue };
    setFilters(updatedFilters);

    // Debounce API call
    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current);
    }

    debounceTimeout.current = setTimeout(() => {
      fetchContacts(false, updatedFilters); // Pass the latest value directly
    }, 500); // Adjust debounce time as needed (500ms in this case)
  };

  return (
    <Container maxWidth={themeStretch ? false : 'lg'}>
      <Box
        sx={{
          padding: 3,
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 3
          }}
        >
          <HeaderBreadcrumbs
            heading="Contacts"
            links={[
              { name: 'Dashboard', href: '/dashboard' },
              { name: 'Contacts', href: '/dashboard/contacts' }
            ]}
          />
          <Box
            sx={{
              display: 'flex',
              gap: 2, // Adds spacing between the buttons
              alignItems: 'center'
            }}
          >
            <Button variant="contained" onClick={handleCreateContact} startIcon={<PersonAdd />}>
              Create Contact
            </Button>
            <Tooltip title="Add Filters">
              <Button
                disableRipple
                color="inherit"
                variant="outlined"
                endIcon={<Icon icon={roundFilterList} />}
                onClick={() => setDrawerOpen(true)}
              >
                Filters
              </Button>
            </Tooltip>
          </Box>
        </Box>

        <CustomPaper>
          <TextField
            variant="outlined"
            placeholder="Search..."
            size="medium"
            sx={{ width: 500 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <GridSearchIcon />
                </InputAdornment>
              )
            }}
            value={filters.search}
            onChange={handleSearch}
          />

          <Box>
            <CommonTable
              rows={contacts}
              columns={columns}
              pageSize={filters.pageSize}
              totalRecords={totalRows}
              loading={loading}
              handlePageChange={(newPage) => setPage(newPage + 1)}
              handlePageSizeChange={(newPageSize) => {
                console.log(newPageSize, 'This is the new pages size');
                setPageSize(newPageSize);
                setFilters({ ...filters, pageSize: newPageSize });
              }}
              onEdit={handleEdit}
              onDelete={(id) => handleDeleteContact(id)}
              onRowClick={handleRowClick}
              showViewSitesButton={true}
              onSiteButtonClick={handleSiteButtonClick}
            />
          </Box>
        </CustomPaper>
        <FiltersDrawer
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          filters={filters}
          setFilters={setFilters}
          onApply={() => {
            setDrawerOpen(false);
            fetchContacts();
          }}
          onClear={clearFilters}
        />
      </Box>
    </Container>
  );
};

export default Contacts;
